import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { Turbo } from '@hotwired/turbo-rails';

// Turn generic HTML elements into links, including support for Turbo, Turbo Streams and keyboard navigation.
// Useful for elements like cards, that already contain <a> element a can't be wrapped in one.
export default class extends Controller {
  static values = {
    url: String,
    turbo: { type: Boolean, default: false },
    turboStream: { type: Boolean, default: false },
    target: String,
    autoOpenType: String,
    autoOpenId: String,
  };

  connect() {
    if (!this.urlValue) {
      return;
    }

    if (!this.element.hasAttribute('role')) {
      this.element.setAttribute('role', 'link');
    }

    if (!this.element.hasAttribute('tabindex')) {
      this.element.setAttribute('tabindex', '0');
    }

    if (!this.element.style.cursor) {
      this.element.style.cursor = 'pointer';
    }

    this.#autoVisitFromUrlParam();
  }

  visit(event) {
    if (!this.urlValue) {
      return;
    }

    if (this.#shouldIgnore(event)) {
      return;
    }

    if (event.type === 'keydown') {
      if (!this.#isActivationKey(event)) {
        return;
      }
      event.preventDefault();
    }

    // Skip non-primary button mouse clicks
    if (event.type === 'click' && event.button !== 0) {
      return;
    }

    this.performVisit();
  }

  performVisit() {
    if (!this.urlValue) {
      return;
    }

    if (this.targetValue) {
      window.open(this.urlValue, this.targetValue);
      return;
    }

    if (this.turboStreamValue) {
      const request = new FetchRequest('get', this.urlValue, {
        responseKind: 'turbo-stream',
      });
      request.perform().catch(console.error);
      return;
    }

    if (this.turboValue) {
      Turbo.visit(this.urlValue, {
        action: 'advance',
      });
      return;
    }

    window.location.assign(this.urlValue);
  }

  #isActivationKey(event) {
    return event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
  }

  #autoVisitFromUrlParam() {
    if (!this.hasAutoOpenTypeValue || !this.hasAutoOpenIdValue) {
      return;
    }

    const url = new URL(window.location.href);
    const openType = url.searchParams.get('linkify_open_type');
    const openId = url.searchParams.get('linkify_open_id');

    if (!openType || !openId) {
      return;
    }

    if (this.autoOpenTypeValue !== openType || this.autoOpenIdValue !== openId) {
      return;
    }

    url.searchParams.delete('linkify_open_type');
    url.searchParams.delete('linkify_open_id');
    window.history.replaceState({}, '', url);

    this.performVisit();
  }

  #shouldIgnore(event) {
    // If some other handler already called preventDefault(), we respect that
    if (event.defaultPrevented) {
      return true;
    }

    // If there's no target (unlikely), we let the click proceed
    const target = event.target;
    if (!target) {
      return false;
    }

    // Clicks inside "interactive" elements → ignore
    const interactiveElement = target.closest(
      'a, button, input, select, textarea, label, [role="button"], [data-linkify-ignore]'
    );
    if (interactiveElement && interactiveElement !== this.element) {
      return true;
    }

    // Clicks inside any other element with a click-> Stimulus action → ignore
    const actionElement = target.closest('[data-action*="click->"]');
    if (actionElement && actionElement !== this.element) {
      return true;
    }

    return false;
  }
}
